'use client';
import Icon from '@/components/ui/AppIcon';

interface Review {
  id: string
  name: string
  type: string
  location?: string
  hardness?: string
  stars: number
  quote: string
  category: 'particulier' | 'zakelijk'
}

const particulierReviews: Review[] = [
  {
    id: 'p-1',
    name: 'Jan V.',
    type: 'Rijtjeshuis',
    location: 'Utrecht',
    hardness: '18 dH',
    stars: 5,
    quote: 'Na de installatie was er helemaal geen kalkaanslag meer. Aanrader voor iedereen die een chemievrij alternatief zoekt.',
    category: 'particulier',
  },
  {
    id: 'p-2',
    name: 'Linda S.',
    type: 'Appartement',
    location: 'Amsterdam',
    hardness: '15 dH',
    stars: 5,
    quote: 'Was ik dit maar eerder tegengekomen. Simpel, effectief, geen gedoe.',
    category: 'particulier',
  },
  {
    id: 'p-3',
    name: 'Mark B.',
    type: 'Vrijstaande woning',
    location: 'Haarlem',
    hardness: '20 dH',
    stars: 5,
    quote: 'Minder poetsen, schonere douchekoppen — en geen zakken zout meer slepen.',
    category: 'particulier',
  },
  {
    id: 'p-4',
    name: 'Jeroen V.',
    type: 'Eengezinswoning',
    location: 'Utrecht',
    hardness: '18 dH',
    stars: 5,
    quote: 'Het verschil was al na twee weken merkbaar. Mijn douchekop zit niet meer vol kalk.',
    category: 'particulier',
  },
  {
    id: 'p-5',
    name: 'Annelies B.',
    type: 'Appartement',
    location: 'Rotterdam',
    hardness: '22 dH',
    stars: 5,
    quote: 'Eindelijk geen witte strepen meer op het glas. Super blij met het resultaat.',
    category: 'particulier',
  },
  {
    id: 'p-6',
    name: 'Mark S.',
    type: 'Vrijstaande woning',
    location: 'Eindhoven',
    hardness: '20 dH',
    stars: 5,
    quote: 'Installatieservice was vlot en professioneel. Binnen een uur alles gedaan.',
    category: 'particulier',
  },
  {
    id: 'p-7',
    name: 'Sandra K.',
    type: 'Rijtjeshuis',
    location: 'Den Haag',
    hardness: '25 dH',
    stars: 5,
    quote: 'Mijn wasmachine en vaatwasser werken veel beter. Minder wasmiddel nodig ook.',
    category: 'particulier',
  },
  {
    id: 'p-8',
    name: 'Peter D.',
    type: 'Eengezinswoning',
    location: 'Breda',
    hardness: '21 dH',
    stars: 5,
    quote: 'Na jaren van kalkaanslag is ons huis eindelijk kalkvrij. Aanrader!',
    category: 'particulier',
  },
  {
    id: 'p-9',
    name: 'Lisa M.',
    type: 'Twee-onder-een-kap',
    location: 'Tilburg',
    hardness: '19 dH',
    stars: 5,
    quote: 'Geen chemicaliën, geen zout — precies wat ik zocht voor ons gezin.',
    category: 'particulier',
  },
  {
    id: 'p-10',
    name: 'Thomas H.',
    type: 'Appartement',
    location: 'Amsterdam',
    hardness: '23 dH',
    stars: 5,
    quote: 'De kranen en tegels blijven nu veel langer schoon. Echt een verschil.',
    category: 'particulier',
  },
  {
    id: 'p-11',
    name: 'Marieke R.',
    type: 'Eengezinswoning',
    location: 'Haarlem',
    hardness: '20 dH',
    stars: 4,
    quote: 'Vriendelijk advies, snelle installatie en direct resultaat. Top bedrijf.',
    category: 'particulier',
  },
]

const zakelijkReviews: Review[] = [
  {
    id: 'z-1',
    name: 'TechInstall B.V.',
    type: 'Installatiebedrijf',
    location: 'Divers',
    stars: 5,
    quote: 'We hebben honderden units geïnstalleerd — katalytisch en dubbele behandeling — met 100% succesratio.',
    category: 'zakelijk',
  },
  {
    id: 'z-2',
    name: 'Hotel De Stadspoort',
    type: 'Horeca',
    location: 'Rotterdam',
    stars: 5,
    quote: 'Sinds de installatie is de stilstandtijd voor reiniging vrijwel geëlimineerd.',
    category: 'zakelijk',
  },
  {
    id: 'z-3',
    name: 'Restaurant De Gouden Leeuw',
    type: 'Horeca',
    location: 'Amsterdam',
    stars: 5,
    quote: 'Onze koffieapparatuur heeft veel minder onderhoud nodig. Bespaart ons uren per week.',
    category: 'zakelijk',
  },
  {
    id: 'z-4',
    name: 'Hotel Marktplein',
    type: 'Hospitality',
    location: 'Maastricht',
    stars: 5,
    quote: 'Voor ons hotel was dit de perfecte oplossing. Gasten klagen niet meer over kalkafzetting.',
    category: 'zakelijk',
  },
  {
    id: 'z-5',
    name: 'TechFlow BV',
    type: 'Industrie',
    location: 'Rotterdam',
    stars: 5,
    quote: 'De industriële installatie verliep vlekkeloos. Zichtbaar resultaat binnen een maand.',
    category: 'zakelijk',
  },
  {
    id: 'z-6',
    name: 'ProClean Services',
    type: 'B2B',
    location: 'Utrecht',
    stars: 5,
    quote: 'Onze stoomketels draaien nu efficiënter. Energiebesparing is merkbaar.',
    category: 'zakelijk',
  },
]

interface ReviewCardProps {
  review: Review
}

function ReviewCard({ review }: ReviewCardProps) {
  return (
    <div className="flex-shrink-0 w-80 mx-3 rounded-2xl p-6 border border-[#0584F2]/15 bg-white shadow-md">
      {/* Stars */}
      <div className="flex items-center gap-1 mb-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Icon
            key={i}
            name="StarIcon"
            size={14}
            variant="solid"
            className={i < review.stars ? 'text-amber-400' : 'text-gray-200'}
          />
        ))}
      </div>
      {/* Quote */}
      <blockquote className="text-sm leading-relaxed mb-4" style={{ color: '#2F4F6E' }}>
        &ldquo;{review.quote}&rdquo;
      </blockquote>
      {/* Author */}
      <div className="pt-4 border-t border-[#0584F2]/10">
        <p className="font-heading font-bold text-sm" style={{ color: '#0F1E2E' }}>{review.name}</p>
        <div className="flex items-center gap-1 mt-1 text-xs" style={{ color: '#2F4F6E' }}>
          <Icon name="MapPinIcon" size={12} className="text-[#33BEBE]" />
          <span>{review.type}{review.location ? `, ${review.location}` : ''}</span>
          {review.hardness && (
            <>
              <span className="mx-1 opacity-40">·</span>
              <Icon name="BeakerIcon" size={12} className="text-[#0584F2]" />
              <span>{review.hardness}</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

interface MarqueeRowProps {
  reviews: Review[]
  reverse?: boolean
}

function MarqueeRow({ reviews, reverse = false }: MarqueeRowProps) {
  const doubled = [...reviews, ...reviews]
  return (
    <div className="overflow-hidden w-full" style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}>
      <div
        className={`flex ${ reverse ? 'animate-marquee-reverse' : 'animate-marquee' } hover:[animation-play-state:paused]`}
        style={{ width: 'max-content' }}
      >
        {doubled.map((review, idx) => (
          <ReviewCard key={`${review.id}-${idx}`} review={review} />
        ))}
      </div>
    </div>
  )
}

export default function ReviewsSection() {
  return (
    <section id="reviews" className="relative py-20 md:py-32 overflow-hidden">
      {/* Subtle radial accents */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-[#33BEBE]/05 via-transparent to-transparent pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-[#0584F2]/04 via-transparent to-transparent pointer-events-none"></div>
      <div className="absolute top-0 left-0 w-[450px] h-[450px] bg-gradient-to-br from-[#33BEBE]/05 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-[#0584F2]/05 to-transparent rounded-full blur-3xl"></div>

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-12 reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-400/30 bg-amber-400/10 backdrop-blur-md">
            <Icon name="StarIcon" size={16} variant="solid" className="text-amber-500" />
            <span className="text-xs font-bold uppercase tracking-widest text-amber-600">
              Klantervaringen
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight bg-gradient-to-r from-[#0F1E2E] via-[#0584F2] to-[#33BEBE] bg-clip-text text-transparent">
            Wat zeggen onze klanten?
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#2F4F6E' }}>
            Ontdek waarom duizenden Nederlanders kiezen voor FrenaCal.
          </p>
        </div>
      </div>

      {/* Marquee rows — full width, outside max-w container */}
      <div className="space-y-5 mb-16">
        {/* Row 1: Particulier — left scroll */}
        <div className="flex items-center gap-3 mb-1 px-6 max-w-7xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#0584F2' }}>Particulier</span>
          <div className="flex-1 h-px bg-gradient-to-r from-[#0584F2]/20 to-transparent"></div>
        </div>
        <MarqueeRow reviews={particulierReviews} />

        {/* Row 2: Zakelijk — right scroll (reverse) */}
        <div className="flex items-center gap-3 mt-4 mb-1 px-6 max-w-7xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#33BEBE' }}>Zakelijk</span>
          <div className="flex-1 h-px bg-gradient-to-r from-[#33BEBE]/20 to-transparent"></div>
        </div>
        <MarqueeRow reviews={zakelijkReviews} reverse />
      </div>

      {/* Stats Bar */}
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 reveal">
          <div className="text-center p-4 md:p-6 rounded-2xl border border-[#0584F2]/15 bg-gradient-to-br from-[#EBF5FF] to-[#E0F0FF]">
            <p className="text-3xl md:text-4xl font-heading font-extrabold bg-gradient-to-r from-[#0584F2] to-[#33BEBE] bg-clip-text text-transparent">250k+</p>
            <p className="text-[10px] md:text-xs uppercase tracking-widest mt-2" style={{ color: '#2F4F6E' }}>Gebruikers wereldwijd</p>
          </div>
          <div className="text-center p-4 md:p-6 rounded-2xl border border-[#33BEBE]/15 bg-gradient-to-br from-[#EBF5FF] to-[#E0F0FF]">
            <p className="text-3xl md:text-4xl font-heading font-extrabold bg-gradient-to-r from-[#33BEBE] to-[#0584F2] bg-clip-text text-transparent">50+</p>
            <p className="text-[10px] md:text-xs uppercase tracking-widest mt-2" style={{ color: '#2F4F6E' }}>Jaar ervaring</p>
          </div>
          <div className="text-center p-4 md:p-6 rounded-2xl border border-[#0584F2]/15 bg-gradient-to-br from-[#EBF5FF] to-[#E0F0FF]">
            <p className="text-3xl md:text-4xl font-heading font-extrabold bg-gradient-to-r from-[#0584F2] to-[#33BEBE] bg-clip-text text-transparent">100%</p>
            <p className="text-[10px] md:text-xs uppercase tracking-widest mt-2" style={{ color: '#2F4F6E' }}>Zoutvrij & stroomvrij</p>
          </div>
          <div className="text-center p-4 md:p-6 rounded-2xl border border-amber-300/30 bg-gradient-to-br from-[#EBF5FF] to-[#E0F0FF]">
            <p className="text-3xl md:text-4xl font-heading font-extrabold bg-gradient-to-r from-amber-500 to-amber-400 bg-clip-text text-transparent">4.8★</p>
            <p className="text-[10px] md:text-xs uppercase tracking-widest mt-2" style={{ color: '#2F4F6E' }}>Gemiddelde beoordeling</p>
          </div>
        </div>
      </div>
    </section>
  )
}